import React, { useState, useEffect, useRef } from 'react';
import {
  Terminal,
  Settings,
  LifeBuoy,
  ChevronRight,
  ArrowRight,
  X,
  Cpu,
  ArrowLeft,
  Upload,
  ChevronDown,
  ArrowUp,
  Loader2
} from 'lucide-react';
import logoNew from './logo-light.png';

// --- Support Modal Component ---
const SupportModal = ({ isOpen, onClose }) => {
  const [status, setStatus] = useState('idle');
  const [charCount, setCharCount] = useState(0);
  const [errors, setErrors] = useState({});
  const MAX_CHARS = 500;

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  if (!isOpen) return null;

  const validate = (formData) => {
    let newErrors = {};
    if (!formData.get('name')) newErrors.name = "Full name is required";
    if (!formData.get('email')) {
      newErrors.email = "Work email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.get('email'))) {
      newErrors.email = "Please enter a valid email address";
    }
    if (!formData.get('product')) newErrors.product = "Please select a product stack";
    if (!formData.get('environment')) newErrors.environment = "Environment selection is required";
    if (!formData.get('severity')) newErrors.severity = "Severity level is required";
    if (!formData.get('message') || formData.get('message').length < 10) {
      newErrors.message = "Please provide at least 10 characters of detail";
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;
    const data = new FormData(form);
    
    const validationErrors = validate(data);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setStatus('submitting');

    try {
      const response = await fetch("https://formspree.io/f/mnjnajrj", {
        method: "POST",
        body: data,
        headers: { 'Accept': 'application/json' }
      });
      
      if (response.ok) {
        setStatus('success');
        setCharCount(0);
        form.reset();
        setTimeout(() => { onClose(); setStatus('idle'); }, 3000);
      } else {
        setStatus('error');
      }
    } catch (error) {
      setStatus('error');
    }
  };

  const ErrorMsg = ({ field }) => errors[field] ? (
    <p className="text-[10px] text-red-500 font-bold mt-1 ml-1 animate-in fade-in slide-in-from-top-1">
      {errors[field]}
    </p>
  ) : null;

  const inputClass = (field) => `w-full bg-slate-50 border ${errors[field] ? 'border-red-400 focus:ring-red-100' : 'border-slate-200 focus:ring-emerald-500/20'} rounded-xl px-4 py-3 text-sm outline-none transition-all`;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative bg-white w-full max-w-lg p-8 rounded-3xl shadow-2xl border border-slate-200 z-10 animate-in zoom-in duration-300">
        <button onClick={onClose} className="absolute top-6 right-6 text-slate-400 hover:text-slate-600 transition-colors"><X size={24} /></button>
        
        <h2 className="text-2xl font-black text-slate-900 mb-6 tracking-tight">Technical Support Hub</h2>
        
        {status === 'success' ? (
          <div className="bg-emerald-50 text-emerald-600 p-8 rounded-2xl text-center">
            <div className="text-4xl mb-2">🚀</div>
            <div className="font-bold text-xl">Ticket Dispatched</div>
            <p className="text-sm">Priority routing confirmed.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <input type="text" name="name" className={inputClass('name')} placeholder="Name" />
                <ErrorMsg field="name" />
              </div>
              <div>
                <input type="email" name="email" className={inputClass('email')} placeholder="Work Email" />
                <ErrorMsg field="email" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <select name="product" className={inputClass('product')} defaultValue="">
                  <option value="" disabled>Product Stack</option>
                  <option value="Transact">Transact (T24)</option>
                  <option value="Infinity">Infinity</option>
                  <option value="TTI">TTI</option>
                  <option value="WSFO">WSFO</option>
                  <option value="FCM">FCM</option>
                </select>
                <ErrorMsg field="product" />
              </div>
              <div>
                <select name="environment" className={inputClass('environment')} defaultValue="">
                  <option value="" disabled>Environment</option>
                  <option value="Production">Production</option>
                  <option value="UAT">UAT</option>
                  <option value="SIT">SIT</option>
                  <option value="Dev">Development</option>
                </select>
                <ErrorMsg field="environment" />
              </div>
            </div>

            <div>
              <select name="severity" className={inputClass('severity')} defaultValue="">
                <option value="" disabled>Severity Level</option>
                <option value="Critical">Critical (P1)</option>
                <option value="High">High (P2)</option>
                <option value="Medium">Medium (P3)</option>
              </select>
              <ErrorMsg field="severity" />
            </div>

            <div className="relative bg-slate-50 border-2 border-dashed border-slate-200 rounded-xl px-4 py-4 text-center group cursor-pointer hover:border-emerald-500/50">
              <input type="file" name="attachment" className="absolute inset-0 opacity-0 cursor-pointer z-10" />
              <span className="text-slate-400 text-xs flex items-center justify-center gap-2 font-medium">
                <Upload size={16} /> Attach logs or snapshots (Optional)
              </span>
            </div>

            <div className="relative">
              <textarea 
                name="message" 
                maxLength={MAX_CHARS}
                onChange={(e) => {
                    setCharCount(e.target.value.length);
                    if(errors.message) setErrors({...errors, message: null});
                }}
                rows="3" 
                className={inputClass('message')} 
                placeholder="Technical details..."
              ></textarea>
              <div className={`absolute bottom-3 right-4 text-[10px] font-bold uppercase ${charCount >= MAX_CHARS ? 'text-red-500' : 'text-slate-400'}`}>
                {charCount} / {MAX_CHARS}
              </div>
              <ErrorMsg field="message" />
            </div>

            <button 
              type="submit" 
              disabled={status === 'submitting'} 
              className="w-full bg-slate-900 text-white font-bold py-4 rounded-xl hover:bg-emerald-600 transition-all active:scale-[0.98] flex items-center justify-center gap-2"
            >
              {status === 'submitting' ? (
                <>
                  <Loader2 className="animate-spin" size={20} />
                  <span>Processing...</span>
                </>
              ) : (
                'Submit Support Case'
              )}
            </button>
            {status === 'error' && <p className="text-red-500 text-center text-xs font-bold">Submission failed. Check your connection.</p>}
          </form>
        )}
      </div>
    </div>
  );
};

// --- Navbar Component ---
const Navbar = ({ onProductSelect, onLogoClick, onContactClick }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);

  return (
    <nav className="fixed w-full z-50 bg-white/90 backdrop-blur-md border-b border-slate-100">
      <div className="max-w-7xl mx-auto px-6 h-20 flex justify-between items-center">
        <div onClick={onLogoClick} className="cursor-pointer flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white shadow-md overflow-hidden border border-slate-50">
            <img src={logoNew} alt="Logo" className="w-full h-full object-contain" />
          </div>
          <span className="text-sm font-black tracking-widest text-slate-900 uppercase">Fin Global Tech</span>
        </div>
        <div className="hidden lg:flex items-center gap-8">
          <div className="relative group">
            <button 
              onMouseEnter={() => setDropdownOpen(true)} 
              className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-widest text-slate-500 hover:text-emerald-600 transition-all"
            >
              Support Areas <ChevronDown size={14} />
            </button>
            {dropdownOpen && (
              <div 
                className="absolute top-full -left-4 w-52 bg-white shadow-2xl border border-slate-100 rounded-2xl py-3 mt-2 animate-in fade-in slide-in-from-top-2" 
                onMouseLeave={() => setDropdownOpen(false)}
              >
                {['Development', 'Implementation', 'Tech Support'].map((item) => (
                  <button 
                    key={item} 
                    onClick={() => { onProductSelect(item); setDropdownOpen(false); }} 
                    className="w-full text-left px-6 py-2.5 text-xs font-bold text-slate-600 hover:bg-emerald-50 hover:text-emerald-600 transition-colors"
                  >
                    {item}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button onClick={onContactClick} className="bg-emerald-500 text-white px-7 py-3 rounded-full font-bold text-xs uppercase hover:bg-emerald-600 transition-all shadow-lg shadow-emerald-500/20">Contact Hub</button>
        </div>
      </div>
    </nav>
  );
};

// --- Main App Component ---
export default function App() {
  const [currentView, setCurrentView] = useState('home');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);

  const sectionRefs = { Development: useRef(null), Implementation: useRef(null), "Tech Support": useRef(null) };

  const specContent = {
    Development: {
      title: "Extensibility & Integration Specs",
      details: "Leveraging Temenos Workbench and Design Frameworks to build 'bank-on-the-go' solutions. Focused on IRIS (RESTful APIs) and High-Volume Interaction (HVI).",
      specs: ["IRIS R24 API Framework", "Temenos Design System (TDS)", "Microservices (Java/Spring Boot)", "OData & Kafka Integration"]
    },
    Implementation: {
      title: "Deployment & Modernization Specs",
      details: "Utilizing the 'Temenos Implementation Methodology' (TIM). Expertise in migrating legacy processes to cloud-native Transact architecture.",
      specs: ["Cloud-Native (Azure/AWS/OCI)", "Database: NuoDB / PostgreSQL", "Data Migration Tool (DMT)", "Zero-Downtime Upgrades"]
    },
    "Tech Support": {
      title: "Managed Services & Operations",
      details: "24/7 support for Temenos Transact and Infinity. Managing the full stack from Application Servers down to the Database layer.",
      specs: ["L1-L3 Support for T24 Core", "Environment Health Checks", "COB Performance Tuning", "Security & Patch Management"]
    }
  };

  useEffect(() => {
    const handleScroll = () => setShowBackToTop(window.scrollY > 400);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (section) => {
    setCurrentView('home');
    setTimeout(() => {
      const element = sectionRefs[section]?.current;
      if (element) {
        const offset = 100;
        const bodyRect = document.body.getBoundingClientRect().top;
        const elementRect = element.getBoundingClientRect().top;
        window.scrollTo({ top: (elementRect - bodyRect) - offset, behavior: 'smooth' });
      }
    }, 100);
  };

  const ServiceCard = ({ icon: Icon, title, description, features }) => (
    <div ref={sectionRefs[title]} className="group bg-white border border-slate-100 p-8 rounded-3xl hover:border-emerald-500/50 hover:shadow-2xl transition-all duration-500 text-left flex flex-col h-full">
      <div className="bg-emerald-50 w-14 h-14 rounded-2xl flex items-center justify-center mb-6 text-emerald-600 group-hover:bg-emerald-500 group-hover:text-white transition-all duration-300">
        <Icon size={28} />
      </div>
      <h3 className="text-2xl font-bold text-slate-900 mb-4">{title}</h3>
      <p className="text-slate-600 mb-6 text-sm leading-relaxed">{description}</p>
      <div className="mb-4 text-[10px] font-bold text-emerald-600 uppercase tracking-widest">Support Areas</div>
      <ul className="space-y-3 mb-8 flex-grow">
        {features.map((f, i) => (
          <li key={i} className="flex items-center gap-3 text-xs text-slate-700 font-semibold">
            <ChevronRight size={14} className="text-emerald-500" /> {f}
          </li>
        ))}
      </ul>
      <button 
        onClick={() => { setCurrentView(title); window.scrollTo({top: 0, behavior: 'smooth'}); }} 
        className="flex items-center gap-2 text-emerald-600 font-bold text-[10px] tracking-widest uppercase group-hover:gap-4 transition-all"
      >
        Read Specifications <ArrowRight size={14} />
      </button>
    </div>
  );

  return (
    <div className="bg-slate-50 min-h-screen font-sans">
      <Navbar 
        onProductSelect={scrollToSection} 
        onLogoClick={() => { setCurrentView('home'); window.scrollTo({top: 0, behavior: 'smooth'}); }} 
        onContactClick={() => setIsModalOpen(true)} 
      />

      {currentView === 'home' ? (
        <>
          <section className="pt-48 pb-32 px-6 text-center bg-gradient-to-b from-blue-50/50 to-transparent">
            <h1 className="text-5xl md:text-7xl font-black text-slate-900 mb-6 tracking-tight leading-[1.1]">
              Engineering the <br /><span className="text-emerald-600">Future of Tech.</span>
            </h1>
            <p className="text-slate-600 text-xl max-w-2xl mx-auto">Specialized delivery for the Temenos ecosystem.</p>
          </section>
          
          <section className="py-24 px-6 max-w-7xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8">
              <ServiceCard icon={Terminal} title="Development" description="IRIS R24 & API-First engineering." features={["IRIS R24 APIs", "TDS Integration", "Microservices"]} />
              <ServiceCard icon={Settings} title="Implementation" description="TIM Methodology for cloud-native stacks." features={["Cloud Migration", "DMT Strategy", "Modernization"]} />
              <ServiceCard icon={LifeBuoy} title="Tech Support" description="24/7 Managed services and COB tuning." features={["COB Tuning", "SLA Management", "L3 Support"]} />
            </div>
          </section>
        </>
      ) : (
        <div className="pt-32 pb-20 px-6 max-w-4xl mx-auto animate-in slide-in-from-bottom-4 duration-700">
          <button 
            onClick={() => scrollToSection(currentView)} 
            className="flex items-center gap-2 text-emerald-600 font-bold mb-8 hover:gap-4 transition-all"
          >
            <ArrowLeft size={20} /> Return to {currentView}
          </button>
          
          <div className="text-left bg-white p-10 border border-slate-100 rounded-[2.5rem] shadow-xl shadow-slate-200/50">
            <span className="text-emerald-500 font-bold tracking-widest text-xs uppercase">Temenos Specialized Partner</span>
            <h1 className="text-4xl md:text-5xl font-black text-slate-900 mt-2 mb-6">{specContent[currentView].title}</h1>
            <p className="text-xl text-slate-600 leading-relaxed mb-10">{specContent[currentView].details}</p>
            
            <div className="grid md:grid-cols-2 gap-4">
              {specContent[currentView].specs.map((spec, i) => (
                <div key={i} className="p-6 bg-slate-50 border border-slate-100 rounded-2xl flex items-center gap-4 hover:bg-white hover:shadow-md transition-all">
                  <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-emerald-600 shadow-sm"><Cpu size={20} /></div>
                  <span className="font-bold text-slate-700">{spec}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {showBackToTop && (
        <button 
          onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})} 
          className="fixed bottom-8 right-8 z-[60] bg-slate-900 text-white p-4 rounded-2xl shadow-2xl hover:bg-emerald-600 hover:-translate-y-1 active:scale-90 transition-all duration-300 animate-in fade-in slide-in-from-bottom-4"
        >
          <ArrowUp size={24} />
        </button>
      )}
      
      {/* --- New Sitemap Footer Section --- */}
      <section className="bg-slate-900 text-white py-20 px-6 mt-24">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-12 text-left">
          <div>
            <h4 className="text-emerald-500 font-black text-xs uppercase tracking-widest mb-6">Service Portfolio</h4>
            <ul className="space-y-4 text-sm text-slate-400 font-medium">
              <li className="hover:text-white cursor-pointer transition-colors">Core Banking</li>
              <li className="hover:text-white cursor-pointer transition-colors">Digital Banking</li>
              <li className="hover:text-white cursor-pointer transition-colors">Wealth Management</li>
              <li className="hover:text-white cursor-pointer transition-colors">AI & Data Science</li>
              <li className="hover:text-white cursor-pointer transition-colors">Cloud Native SaaS</li>
            </ul>
          </div>
          <div>
            <h4 className="text-emerald-500 font-black text-xs uppercase tracking-widest mb-6">Industry Solutions</h4>
            <ul className="space-y-4 text-sm text-slate-400 font-medium">
              <li className="hover:text-white cursor-pointer transition-colors">Retail & Business</li>
              <li className="hover:text-white cursor-pointer transition-colors">Corporate Banking</li>
              <li className="hover:text-white cursor-pointer transition-colors">Islamic Banking</li>
              <li className="hover:text-white cursor-pointer transition-colors">Credit Unions</li>
              <li className="hover:text-white cursor-pointer transition-colors">Regionalized Apps</li>
            </ul>
          </div>
          <div>
            <h4 className="text-emerald-500 font-black text-xs uppercase tracking-widest mb-6">Partner Ecosystem</h4>
            <ul className="space-y-4 text-sm text-slate-400 font-medium">
              <li className="hover:text-white cursor-pointer transition-colors">Become a Partner</li>
              <li className="hover:text-white cursor-pointer transition-colors">Delivery Partners</li>
              <li className="hover:text-white cursor-pointer transition-colors">Technology Alliances</li>
              <li className="hover:text-white cursor-pointer transition-colors">Solution Providers</li>
              <li className="hover:text-white cursor-pointer transition-colors">Developer Portal</li>
            </ul>
          </div>
          <div>
            <h4 className="text-emerald-500 font-black text-xs uppercase tracking-widest mb-6">Corporate Hub</h4>
            <ul className="space-y-4 text-sm text-slate-400 font-medium">
              <li className="hover:text-white cursor-pointer transition-colors">Our Story</li>
              <li className="hover:text-white cursor-pointer transition-colors">Careers</li>
              <li className="hover:text-white cursor-pointer transition-colors">Newsroom</li>
              <li className="hover:text-white cursor-pointer transition-colors">Global Offices</li>
              <li className="hover:text-white cursor-pointer transition-colors text-emerald-400 font-bold">Newsletter Sign-up</li>
            </ul>
          </div>
        </div>
      </section>

      <footer className="py-10 px-6 bg-slate-900 border-t border-slate-800 text-center text-slate-500 text-[10px] font-bold tracking-widest uppercase">
        © 2026 Fin Global Tech. All Rights Reserved.
      </footer>
      
      <SupportModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
}